// Private pitch page: same landing pattern as Home (grid + rail) with a recipient
// banner, curated projects first, then the remaining projects (order configurable).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import LandingGrid from "../designSystem/components/home/LandingGrid";
import ProjectRail from "../designSystem/components/home/ProjectRail";
import { baseProjects } from "../designSystem/data/projectsData";
import { PRIVATE_RECIPIENTS } from "./privateRecipients";

const RAIL_LEAVE_DELAY_MS = 800;

function normalizeTargetUrl(url) {
  if (url == null) return "";
  const s = String(url).trim();
  if (!s) return "";
  return s.startsWith("/") ? s : `/${s}`;
}

function PrivateForPage() {
  const { slug } = useParams();
  const config = PRIVATE_RECIPIENTS[slug];

  const { curated, rest, flatProjects } = useMemo(() => {
    if (!config) {
      return { curated: [], rest: [], flatProjects: [] };
    }
    const byUrl = new Map(
      baseProjects.map((p) => [normalizeTargetUrl(p.targetUrl), p])
    );
    const curatedOrdered = [];
    for (const u of config.curatedTargetUrls) {
      const p = byUrl.get(normalizeTargetUrl(u));
      if (p) curatedOrdered.push(p);
    }
    const curatedSet = new Set(curatedOrdered.map((p) => normalizeTargetUrl(p.targetUrl)));

    let restList;
    if (
      Array.isArray(config.additionalTargetUrls) &&
      config.additionalTargetUrls.length > 0
    ) {
      const restOrdered = [];
      for (const u of config.additionalTargetUrls) {
        const key = normalizeTargetUrl(u);
        if (curatedSet.has(key)) continue;
        const p = byUrl.get(key);
        if (p) restOrdered.push(p);
      }
      const restKeys = new Set(
        restOrdered.map((p) => normalizeTargetUrl(p.targetUrl))
      );
      const remainder = baseProjects.filter((p) => {
        const key = normalizeTargetUrl(p.targetUrl);
        return !curatedSet.has(key) && !restKeys.has(key);
      });
      restList = [...restOrdered, ...remainder];
    } else {
      restList = baseProjects.filter(
        (p) => !curatedSet.has(normalizeTargetUrl(p.targetUrl))
      );
    }

    return {
      curated: curatedOrdered,
      rest: restList,
      flatProjects: [...curatedOrdered, ...restList],
    };
  }, [config]);

  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const leaveTimer = useRef(null);

  useEffect(() => {
    setHoveredIndex(null);
    setFocusedIndex(null);
  }, [slug]);

  const clearLeaveTimer = () => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  };

  const handleHoverIndex = useCallback((idx) => {
    clearLeaveTimer();
    setHoveredIndex(idx);
  }, []);

  const handleGridMouseLeave = useCallback(() => {
    clearLeaveTimer();
    leaveTimer.current = setTimeout(() => {
      setHoveredIndex(null);
    }, RAIL_LEAVE_DELAY_MS);
  }, []);

  useEffect(() => () => clearLeaveTimer(), []);

  const railIndex =
    hoveredIndex != null
      ? hoveredIndex
      : focusedIndex != null
        ? focusedIndex
        : null;

  const railProject =
    railIndex != null ? flatProjects[railIndex] || null : null;

  const mailtoHref = config
    ? `mailto:${config.email}?subject=${encodeURIComponent(config.emailSubject)}`
    : "#";

  if (!config) {
    return (
      <main
        className="
          flex flex-1 flex-col min-h-0 w-full justify-center
          bg-white dark:bg-black
          pt-[72px] box-border
          transition-colors duration-500
        "
      >
        <div className="px-6 lg:px-12 py-16 max-w-xl">
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-content-muted">
            Private link
          </p>
          <h1 className="mt-4 text-[1.5rem] lg:text-[1.75rem] leading-[1.15] font-normal tracking-[-0.01em] text-black dark:text-white">
            This page is not available.
          </h1>
          <p className="mt-4 text-sm font-light leading-relaxed text-content-secondary dark:text-neutral-400 max-w-[50ch]">
            The URL may be mistyped or this share link is no longer in use.
          </p>
          <Link
            to="/"
            data-cursor="pointer"
            className="
              inline-flex mt-8 items-center gap-3
              px-4 py-3 border border-black dark:border-white
              font-mono text-[11px] tracking-[0.18em] uppercase text-black dark:text-white
              hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black
              transition-colors duration-150 clickable cursor-none
            "
          >
            Back to work <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main
      className="
        flex flex-1 flex-col min-h-0 w-full
        bg-white dark:bg-black
        pt-[72px] box-border
        transition-colors duration-500
      "
    >
      <div
        className="
          flex flex-1 flex-col min-h-0 w-full
          max-w-[1440px] mx-auto
          px-6 lg:px-12
          py-4 lg:py-6
          gap-6 lg:gap-10
          lg:flex-row
        "
        onMouseEnter={clearLeaveTimer}
        onMouseLeave={handleGridMouseLeave}
      >
        <div
          className="
            flex flex-1 flex-col min-h-0 min-w-0
            lg:border-r lg:border-border lg:dark:border-neutral-800
            lg:pr-8
          "
        >
          <div
            className="
              flex-1 min-h-0 overflow-y-auto overflow-x-hidden
              overscroll-y-contain
              [scrollbar-gutter:stable]
            "
          >
            <RecipientBanner
              companyName={config.companyName}
              headline={config.headline}
              body={config.body}
              resumeUrl={config.resumeUrl}
              resumeDownloadName={config.resumeDownloadName}
              mailtoHref={mailtoHref}
            />

            <div className="mt-10 lg:mt-12 border-t border-border-subtle dark:border-neutral-800 pt-8 lg:pt-10">
              <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-content-muted mb-4 lg:mb-5">
                Relevant for the role · {curated.length}
              </p>
              <LandingGrid
                projects={curated}
                hoveredIndex={hoveredIndex}
                onHoverIndex={handleHoverIndex}
                focusedIndex={focusedIndex}
                onFocusIndex={setFocusedIndex}
                indexOffset={0}
                totalProjectCount={flatProjects.length}
              />
            </div>

            <div className="mt-10 lg:mt-14 border-t border-border-subtle dark:border-neutral-800 pt-8 lg:pt-10 pb-4">
              <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-content-muted mb-4 lg:mb-5">
                Additional projects · {rest.length}
              </p>
              <LandingGrid
                projects={rest}
                hoveredIndex={hoveredIndex}
                onHoverIndex={handleHoverIndex}
                focusedIndex={focusedIndex}
                onFocusIndex={setFocusedIndex}
                indexOffset={curated.length}
                totalProjectCount={flatProjects.length}
              />
            </div>
          </div>
        </div>

        <div
          className="
            shrink-0 w-full lg:w-[320px] lg:max-w-[320px]
            lg:flex lg:flex-col lg:min-h-0
            border-t border-border-subtle dark:border-neutral-800
            lg:border-t-0
            pt-6 lg:pt-0
          "
        >
          <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:overscroll-y-contain">
            <ProjectRail
              project={railProject}
              index={railIndex ?? 0}
              total={flatProjects.length}
            />
          </div>
        </div>
      </div>
    </main>
  );
}

function RecipientBanner({
  companyName,
  headline,
  body,
  resumeUrl,
  resumeDownloadName,
  mailtoHref,
}) {
  return (
    <header className="max-w-[52ch]">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-content-muted">
          For {companyName}
          <span className="text-content-muted/70 dark:text-neutral-600">
            {" "}
            · private link · not for distribution
          </span>
        </p>
      </div>

      <h1
        className="
          mt-5 lg:mt-6
          text-[1.5rem] sm:text-[1.625rem] lg:text-[1.875rem]
          leading-[1.15] tracking-[-0.01em] font-normal
        "
      >
        <span className="text-black dark:text-white">{headline}</span>
      </h1>

      <p
        className="
          mt-4 lg:mt-5
          text-[14px] lg:text-[15px] leading-[1.65]
          text-content-secondary dark:text-neutral-400 font-light
        "
      >
        {body}
      </p>

      <div className="mt-6 lg:mt-7 flex flex-wrap items-center gap-3">
        <a
          href={resumeUrl}
          download={resumeDownloadName}
          target="_blank"
          rel="noopener noreferrer"
          data-cursor="pointer"
          className="
            clickable cursor-none
            inline-flex items-center justify-between gap-3
            px-4 py-[10px]
            bg-black text-white dark:bg-white dark:text-black
            font-mono text-[11px] tracking-[0.18em] uppercase
            border border-black dark:border-white
            hover:opacity-85 transition-opacity duration-150
          "
        >
          <span>Download résumé</span>
          <span aria-hidden="true">&darr;</span>
        </a>
        <a
          href={mailtoHref}
          data-cursor="pointer"
          className="
            clickable cursor-none
            inline-flex items-center justify-between gap-3
            px-4 py-[10px]
            border border-black dark:border-white
            font-mono text-[11px] tracking-[0.18em] uppercase
            text-black dark:text-white
            transition-colors duration-150
            hover:bg-black hover:text-white
            dark:hover:bg-white dark:hover:text-black
          "
        >
          <span>Email me</span>
          <span aria-hidden="true">&#x2197;</span>
        </a>
      </div>
    </header>
  );
}

export default PrivateForPage;
